import * as React from 'react';

import Alert from '@mui/material/Alert';
import AuthImage from '../components/AuthImage';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import { CLOCK_STATUS, getMyClockRequests, requestPhotoPath } from '../lib/clockLocations';

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB') : '');

export default function MyClockRequests({ reloadKey, onAddClock }) {
    const [state, setState] = React.useState({ loading: true, clocks: [], error: '' });

    React.useEffect(() => {
        let cancelled = false;
        setState((old) => ({ ...old, loading: true }));
        getMyClockRequests().then((response) => {
            if (cancelled) return;
            setState(response.ok
                ? { loading: false, clocks: response.clocks, error: '' }
                : { loading: false, clocks: [], error: response.message });
        });
        return () => { cancelled = true; };
    }, [reloadKey]);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="subtitle1">Requested clock locations</Typography>
                <Button variant="outlined" size="small" onClick={onAddClock}>Request a clock</Button>
            </Box>

            {state.loading ? <Box sx={{ textAlign: 'center', p: 2 }}><CircularProgress size={24} /></Box> : null}
            {state.error ? <Alert severity="error" sx={{ mt: 1 }}>{state.error}</Alert> : null}
            {!state.loading && !state.error && state.clocks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    You haven't requested any clocks yet. Know a clock that is missing from the map? Request it and an
                    admin will check it.
                </Typography>
            ) : null}

            <List disablePadding>
                {state.clocks.map((clock) => {
                    const status = CLOCK_STATUS[clock.status] || { label: clock.status, color: 'default' };
                    const details = [clock.address, clock.ward].filter(Boolean).join(', ');
                    return (
                        <ListItem key={clock.id} alignItems="flex-start" disableGutters sx={{ gap: 1.5 }}>
                            <AuthImage
                                path={clock.photo_ids && clock.photo_ids.length > 0 ? requestPhotoPath(clock.photo_ids[0]) : null}
                                alt={clock.name}
                            />
                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>{clock.name}</Typography>
                                {details ? <Typography variant="body2" color="text.secondary">{details}</Typography> : null}
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Requested {formatDate(clock.created_at)}
                                </Typography>
                                <Chip size="small" label={status.label} color={status.color} sx={{ mt: 0.5 }} />
                                {clock.review_note ? (
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        Admin note: {clock.review_note}
                                    </Typography>
                                ) : null}
                            </Box>
                        </ListItem>
                    );
                })}
            </List>
        </Box>
    );
}
